import React, { useEffect, useMemo, useState } from "react";
import {
  BulbOutlined,
  CloseOutlined,
  LogoutOutlined,
  RightOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ElsaAdaptiveEmblem from "../components/branding/ElsaAdaptiveEmblem";

const getNodeText = (title) => {
  if (title == null) return "";
  if (typeof title === "string" || typeof title === "number") {
    return String(title);
  }
  if (Array.isArray(title)) return title.map(getNodeText).join(" ");
  if (title.props?.children) return getNodeText(title.props.children);
  return "";
};

const filterTree = (nodes, query) => {
  if (!query) return { tree: nodes, openKeys: [] };

  const openKeys = [];
  const walk = (items) =>
    items
      .map((node) => {
        const children = node.children ? walk(node.children) : [];
        const matches = getNodeText(node.title).toLowerCase().includes(query);

        if (matches) {
          if (node.children?.length) openKeys.push(node.key);
          return node;
        }

        if (children.length) {
          openKeys.push(node.key);
          return { ...node, children };
        }

        return null;
      })
      .filter(Boolean);

  return { tree: walk(nodes), openKeys };
};

const highlight = (text, query) => {
  if (!query) return text;

  const index = text.toLowerCase().indexOf(query);
  if (index < 0) return text;

  return (
    <>
      {text.slice(0, index)}
      <span className="ea-nav__hl">
        {text.slice(index, index + query.length)}
      </span>
      {text.slice(index + query.length)}
    </>
  );
};

const containsKey = (node, key) => {
  if (!key) return false;
  if (node.key === key) return true;
  return Boolean(node.children?.some((child) => containsKey(child, key)));
};

const countLeaves = (node) => {
  if (!node.children?.length) return 1;
  return node.children.reduce((total, child) => total + countLeaves(child), 0);
};

const NavNode = ({
  node,
  level,
  query,
  expanded,
  onToggle,
  selectedKey,
  onSelect,
}) => {
  const hasChildren = Boolean(node.children?.length);
  const isOpen = expanded.includes(node.key);
  const isActive = selectedKey === node.key;
  const isActivePath = containsKey(node, selectedKey);
  const label = getNodeText(node.title);

  if (!hasChildren) {
    return (
      <button
        type="button"
        className={`ea-nav__leaf-row ${
          level === 0 ? "ea-nav__row" : "ea-nav__leaf"
        } ea-nav__level-${level}${isActive ? " active" : ""}`}
        onClick={() => onSelect(node)}
        aria-current={isActive ? "page" : undefined}
      >
        {node.icon ? (
          <span className="ea-nav__ic">{node.icon}</span>
        ) : (
          <span className="ea-nav__dot" />
        )}
        <span className="ea-nav__lbl">
          {query ? highlight(label, query) : node.title}
        </span>
        {isActive ? (
          <span className="ea-nav__active-mark" aria-hidden="true" />
        ) : null}
      </button>
    );
  }

  return (
    <div
      className={`ea-nav__group ea-nav__group--level-${level}${
        isActivePath ? " active-path" : ""
      }`}
    >
      <button
        type="button"
        className={`ea-nav__row ea-nav__level-${level}${
          isOpen ? " exp" : ""
        }${isActivePath ? " active-path" : ""}`}
        onClick={() => onToggle(node.key)}
        aria-expanded={isOpen}
      >
        {node.icon ? (
          <span className="ea-nav__ic">{node.icon}</span>
        ) : (
          <span className="ea-nav__dot" />
        )}
        <span className="ea-nav__lbl">
          {query ? highlight(label, query) : node.title}
        </span>
        <span className="ea-nav__count">{countLeaves(node)}</span>
        <RightOutlined className="ea-nav__cv" />
      </button>

      {isOpen ? (
        <div className="ea-nav__sub">
          {node.children.map((child) => (
            <NavNode
              key={child.key}
              node={child}
              level={level + 1}
              query={query}
              expanded={expanded}
              onToggle={onToggle}
              selectedKey={selectedKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default function MobileNavDrawer({
  open,
  onClose,
  treeData = [],
  selectedKey,
  defaultExpandedKeys = [],
  onSelect,
  user,
  getImageUrl,
  onProfile,
  onLogout,
  onToggleTheme,
  notificationSlot,
  logoSrc,
}) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(defaultExpandedKeys);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setExpanded((current) => [
      ...new Set([...current, ...defaultExpandedKeys]),
    ]);
  }, [defaultExpandedKeys, open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  const normalizedQuery = query.trim().toLowerCase();
  const { tree, openKeys } = useMemo(
    () => filterTree(treeData, normalizedQuery),
    [normalizedQuery, treeData]
  );
  const effectiveExpanded = normalizedQuery ? openKeys : expanded;

  const toggle = (key) => {
    setExpanded((current) =>
      current.includes(key)
        ? current.filter((expandedKey) => expandedKey !== key)
        : [...current, key]
    );
  };

  const imageUrl =
    user?.profile_image && getImageUrl
      ? getImageUrl(user.profile_image)
      : undefined;
  const initials = String(user?.name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <div className={`ea-nav-host${open ? " open" : ""}`} aria-hidden={!open}>
      <div className="ea-nav-scrim" onClick={onClose} />
      <aside className="ea-nav" aria-label="Menu principal">
        <div className="ea-nav__accent" />
        <div className="ea-nav__head">
          {logoSrc ? (
            <img className="ea-nav__logo" src={logoSrc} alt="ELSA GESTION" />
          ) : (
            <ElsaAdaptiveEmblem className="ea-nav__logo" />
          )}
          <span className="ea-nav__brand">
            <b>Elsa Gestion</b>
            <small>Navigation mobile</small>
          </span>
          <button
            type="button"
            className="ea-nav__close"
            onClick={onClose}
            aria-label="Fermer le menu"
          >
            <CloseOutlined />
          </button>
        </div>

        <div className="ea-nav__search">
          <SearchOutlined />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Rechercher un module..."
            aria-label="Rechercher un module"
          />
          {query ? (
            <button
              type="button"
              className="ea-nav__searchclear"
              onClick={() => setQuery("")}
              aria-label="Effacer la recherche"
            >
              <CloseOutlined />
            </button>
          ) : null}
        </div>

        <div className="ea-nav__scroll">
          <div className="ea-nav__sectionlbl">
            {normalizedQuery ? "Résultats" : "Menu principal"}
          </div>
          {tree.length ? (
            tree.map((node) => (
              <NavNode
                key={node.key}
                node={node}
                level={0}
                query={normalizedQuery}
                expanded={effectiveExpanded}
                onToggle={toggle}
                selectedKey={selectedKey}
                onSelect={onSelect}
              />
            ))
          ) : (
            <div className="ea-nav__empty">
              Aucun module pour &quot;{query}&quot;
            </div>
          )}
        </div>

        <div className="ea-nav__foot">
          <button
            type="button"
            className="ea-nav__profile"
            onClick={onProfile}
          >
            <span className="ea-nav__av">
              {imageUrl ? (
                <img src={imageUrl} alt="" />
              ) : (
                initials || <UserOutlined />
              )}
            </span>
            <span className="ea-nav__profile-t">
              <b>{user?.name || "Utilisateur"}</b>
              <small>{user?.role || "Rôle non renseigné"}</small>
            </span>
            <RightOutlined />
          </button>

          <div className="ea-nav__foot-row">
            {notificationSlot ? (
              <span className="ea-nav__foot-slot">{notificationSlot}</span>
            ) : null}
            {onToggleTheme ? (
              <button
                type="button"
                className="ea-nav__foot-btn"
                onClick={onToggleTheme}
              >
                <BulbOutlined />
                Thème
              </button>
            ) : null}
            <button
              type="button"
              className="ea-nav__foot-btn danger"
              onClick={onLogout}
            >
              <LogoutOutlined />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
